import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { X, Save, MessageSquare, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CATEGORIES = [
  { name: 'Life', color: 'bg-blue-500/20', textColor: 'text-blue-300', borderColor: 'border-blue-500/30' },
  { name: 'Work', color: 'bg-purple-500/20', textColor: 'text-purple-300', borderColor: 'border-purple-500/30' },
  { name: 'Family', color: 'bg-pink-500/20', textColor: 'text-pink-300', borderColor: 'border-pink-500/30' },
  { name: 'Relationship', color: 'bg-red-500/20', textColor: 'text-red-300', borderColor: 'border-red-500/30' },
  { name: 'Financial', color: 'bg-green-500/20', textColor: 'text-green-300', borderColor: 'border-green-500/30' },
  { name: 'Health', color: 'bg-orange-500/20', textColor: 'text-orange-300', borderColor: 'border-orange-500/30' },
  { name: 'Education', color: 'bg-indigo-500/20', textColor: 'text-indigo-300', borderColor: 'border-indigo-500/30' },
  { name: 'Personal', color: 'bg-yellow-500/20', textColor: 'text-yellow-300', borderColor: 'border-yellow-500/30' },
  { name: 'Spiritual', color: 'bg-cyan-500/20', textColor: 'text-cyan-300', borderColor: 'border-cyan-500/30' },
  { name: 'Other', color: 'bg-gray-500/20', textColor: 'text-gray-300', borderColor: 'border-gray-500/30' },
];

function ObjectiveEditModal({ objective, isOpen, onClose, onSave, userRole }) {
  const [objectiveText, setObjectiveText] = useState('');
  const [category, setCategory] = useState('Life');
  const [timeline, setTimeline] = useState('');
  const [status, setStatus] = useState('active');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);

  useEffect(() => {
    if (objective && isOpen) {
      setObjectiveText(objective.objective || '');
      setCategory(objective.category || 'Life');
      setTimeline(objective.timeline || '');
      setStatus(objective.status || 'active');
      setComments(objective.comments || []);
      setNewComment('');
    }
  }, [objective, isOpen]);

  const handleSave = async () => {
    if (!objectiveText.trim()) {
      toast('Please enter an objective');
      return;
    }

    setLoading(true);
    try {
      const endpoint = userRole === 'client' 
        ? `${API_URL}/client/objectives/${objective.id}`
        : `${API_URL}/objectives/${objective.id}`;
      
      const response = await axios.put(endpoint, {
        objective: objectiveText.trim(),
        category,
        timeline: timeline.trim() || null,
        status
      });

      // Update comments if they were included in response
      if (response.data.comments) {
        setComments(response.data.comments);
      }

      onSave(response.data);
      onClose();
    } catch (error) {
      console.error('Error saving objective:', error);
      toast.error('Failed to save objective');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      return;
    }

    setCommentLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/objectives/${objective.id}/comments`,
        { comment: newComment.trim() }
      );

      setComments([...comments, response.data]);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !objective) return null;

  const categoryInfo = CATEGORIES.find(c => c.name === category) || CATEGORIES[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div 
        className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--border-color)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <h2 className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            Edit Objective
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition"
            style={{ color: 'var(--text-secondary)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Objective Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Objective
              </label>
              <textarea
                value={objectiveText}
                onChange={(e) => setObjectiveText(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border transition focus:outline-none resize-none"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)'
                }}
                rows="3"
                placeholder="Enter your objective..."
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--accent)';
                  e.target.style.boxShadow = '0 0 0 2px rgba(212, 165, 116, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-color)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border transition focus:outline-none"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--accent)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(212, 165, 116, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border-color)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.name} value={cat.name} style={{ backgroundColor: 'var(--bg-primary)' }}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border transition focus:outline-none"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--accent)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(212, 165, 116, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border-color)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="active" style={{ backgroundColor: 'var(--bg-primary)' }}>Active</option>
                  <option value="completed" style={{ backgroundColor: 'var(--bg-primary)' }}>Completed</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Timeline (optional)
              </label>
              <input
                type="text"
                value={timeline}
                onChange={(e) => setTimeline(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border transition focus:outline-none"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)'
                }}
                placeholder="e.g., 3 months, by end of year"
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--accent)';
                  e.target.style.boxShadow = '0 0 0 2px rgba(212, 165, 116, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-color)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          {/* Comments Section */}
          <div className="border-t pt-6" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5" style={{ color: 'var(--accent)' }} />
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                Comments & Progress
              </h3>
            </div>

            {/* Comments List */}
            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {comments.length === 0 ? (
                <p className="text-sm text-center py-4" style={{ color: 'var(--text-tertiary)' }}>
                  No comments yet. Add your first comment to track progress!
                </p>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--bg-primary)' }}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                        {comment.comment}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(comment.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Add Comment */}
            <div className="flex gap-2">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handleAddComment();
                  }
                }}
                className="flex-1 px-4 py-2 rounded-lg border transition focus:outline-none resize-none"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)'
                }}
                rows="2"
                placeholder="Add a comment or note about your progress... (Cmd/Ctrl+Enter to save)"
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--accent)';
                  e.target.style.boxShadow = '0 0 0 2px rgba(212, 165, 116, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-color)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                onClick={handleAddComment}
                disabled={!newComment.trim() || commentLoading}
                className="px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: 'var(--accent)',
                  color: '#1a1816'
                }}
                onMouseEnter={(e) => {
                  if (!commentLoading && newComment.trim()) {
                    e.target.style.backgroundColor = 'var(--accent-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--accent)';
                }}
              >
                {commentLoading ? '...' : 'Add'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg font-medium transition"
            style={{
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-color)'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !objectiveText.trim()}
            className="px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{
              backgroundColor: 'var(--accent)',
              color: '#1a1816'
            }}
            onMouseEnter={(e) => {
              if (!loading && objectiveText.trim()) {
                e.target.style.backgroundColor = 'var(--accent-hover)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--accent)';
            }}
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ObjectiveEditModal;

